import React from "react";
import { AccordionDetails, Divider, AccordionSummary, Accordion, Grid, TextareaAutosize, Button, TextField, IconButton, InputAdornment, Box } from "@mui/material";

import { GridCloseIcon, GridExpandMoreIcon } from "@mui/x-data-grid";
import { Delete } from "@mui/icons-material";
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';

import globalStyles from "../../../../../assets/styles/global.module.scss";
import styles from "../../../../../assets/styles/componentStyles/templateLibraryStyles/modifyWhatsAppTemplate/whatsAppTemplateForm.module.scss";

import { checkFlagError, handleKeyDownForFormatting, hasConsecutiveNewlinesInBody, hasTooManyBodyEmojis, isInteractiveTemplate, isTemplateCreated } from "../../constant";
import { areBodyVariablesModified, canUpdateTemplate, displayFlexCursorPointerClassName, doesBodyTextStartOrEndWithVariable, getTextCharacterLimit, getVariableRegex, isTemplateCategoryAuthentication, isTemplateCategoryMarketing, maxChars, renderBodyVariableEdgeWarning, renderInvalidVarParamWarning, renderTooManyVariableParameterWarning, renderWarningOfModifiedVar } from "../../../channels/global/whatsapp/templates/utils";
import { useTemplateLibraryStore } from "../../../../../store/templateLibraryStore";
import { useSearchParams } from "react-router-dom";
import Required from "../../../../utility/required";
import EmojiPickerButton from "../../utils/EmojiPickerButton";
import insertTextAtCursor from "../../utils/insertTextAtCursor";
import TouchTooltip from "../../utils/TouchTooltip";
import VariableDisclaimer from "../../utils/VariableDisclaimer";
import SampleVariableKeyLabel from "../../utils/SampleVariableKeyLabel";

const WhatsAppTemplateFormBody = (props: any) => {
    const { handleChangeText, handleAddBodyVariableManually, removeVariableData, handleAddField, handleChange, handleRemoveField, 
        textareaRef, checkTypeOfVariableValidation, handleSecurityRecommendationChange, handleFormatting, registerTextarea
    } = props;

    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const templateStatusDetails = useTemplateLibraryStore((state) => state.templateStatusDetails);
    const existingTemplateData = useTemplateLibraryStore((state) => state.existingTemplateData);
    const setCreateTemplatePayload = useTemplateLibraryStore((state) => state.setCreateTemplatePayload);
    const interactiveTypeList = useTemplateLibraryStore((state) => state.interactiveTypeList);
    const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
    const whatsappTemplateTypes = useTemplateLibraryStore((state) => state.whatsappTemplateTypes);

    const [searchParams] = useSearchParams();
    let draft_id = searchParams.get('draft_id');

    const isAuthenticationTemplate = isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);
    const shouldShowAddVariableButton = !isTemplateCreated(createTemplatePayload) && !isAuthenticationTemplate;
    const isMarketingTemplate = isTemplateCategoryMarketing(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id);
    
    const bodyTextValue = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? "";
    const showTooManyEmojisError = isMarketingTemplate && hasTooManyBodyEmojis(bodyTextValue);
    const showConsecutiveNewlinesError = !isInteractiveTemplate(createTemplatePayload) && hasConsecutiveNewlinesInBody(bodyTextValue);

    const formattingIconClassName = `${displayFlexCursorPointerClassName} ${styles.formattingIcon} ${isAuthenticationTemplate ? styles.disabledIcon : ''}`

    const handleBodyTextDisabled = () => {
        if (!createTemplatePayload?.whatsapp_template_id && !draft_id) {
            return isAuthenticationTemplate;
        }

        return (
            (!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draft_id))
            || isAuthenticationTemplate
        )
    }

    const handleBodySampleVariableTextDisabled = () => {
        return (
            (!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draft_id)
            || (createTemplatePayload?.whatsapp_template_id && isAuthenticationTemplate)) ? true : false
        )
    }

    const handleBodyTextChange = (value: string, cursorPosition?: number) => {

        if (value.endsWith('{{')) {
            handleAddBodyVariableManually(value, "body");
            return;
        }

        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const transformedValue = value.replace(getVariableRegex(latestPayload?.flag_named_parameter), '*');
        const maximumBodyCharacterLimit = getTextCharacterLimit(latestPayload, interactiveTypeList, "body text limit") ?? maxChars;

        if (transformedValue.length > maximumBodyCharacterLimit) {
            return;
        }

        const finalValues = removeVariableData(value, "body");
        handleChangeText({ target: { value } }, 'format');
        setCreateTemplatePayload({
            ...latestPayload,
            whatsapp_template_data: {
                ...latestPayload?.whatsapp_template_data,
                whatsapp_template_body: {
                    ...latestPayload?.whatsapp_template_data?.whatsapp_template_body,
                    whatsapp_template_body_value: finalValues.textAreaValue,
                    whatsapp_template_body_variable_data: finalValues.variableData,
                },
            },
        });

        if (cursorPosition !== undefined && textareaRef.current) {
            requestAnimationFrame(() => {
                textareaRef.current?.focus();
                textareaRef.current?.setSelectionRange(cursorPosition, cursorPosition);
            });
        }
    };

    const handleInsertEmoji = (emoji: string, selection: { start: number; end: number } | null) => {
        const latestPayload = useTemplateLibraryStore.getState().createTemplatePayload;
        const bodyValue = latestPayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? "";
        const start = selection?.start ?? bodyValue.length;
        const end = selection?.end ?? bodyValue.length;
        const result = insertTextAtCursor(bodyValue, emoji, start, end);

        handleBodyTextChange(result.value, result.cursorPosition);
    };

    return (
        <Accordion defaultExpanded={true} className={`${styles.accordion}`}>
            
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" className={styles.accordion__summary}>
                
                <Grid container justifyContent={"space-between"} alignItems={"center"}>
                    <span className={styles.accordion__summary__text}>
                        Body <Required />
                    </span>

                    <span className={styles.accordion__summary__value}>
                        {`${bodyTextValue?.replace(/{{\d+}}/g, '*')?.length}/${getTextCharacterLimit(createTemplatePayload, interactiveTypeList, "body text limit")}`}
                    </span>
                </Grid>

            </AccordionSummary>

            <Divider sx={{ width: "100%", opacity: "1" }} />

            <AccordionDetails className={styles.accordion__details}>
                
                <Grid container xs={12}>

                    <Grid item xs={12} className={styles.whatsappTemplateFormBody__bodyTextContainer}>

                        {/* Formatting */}
                        <Grid container className={styles.formattingContainer}>

                            {/* Formatting Buttons */}
                            <Grid item sx={{ width: "auto", flexShrink: 0 }}>
                                <Box className={styles.formattingToolbarGroup}>
                                    
                                    <Box className={styles.formattingToolbarIcons}>
                                        <TouchTooltip title={"Bold (Ctrl + B)"}>
                                            <FormatBoldIcon 
                                                className={formattingIconClassName} 
                                                onClick={() => handleFormatting('bold', 'body')} 
                                            />
                                        </TouchTooltip>

                                        <TouchTooltip title={"Italic (Ctrl + I)"}>
                                            <FormatItalicIcon 
                                                className={formattingIconClassName} 
                                                onClick={() => handleFormatting('italic', 'body')} 
                                            />
                                        </TouchTooltip>

                                        <TouchTooltip title={"Strikethrough (Ctrl + S)"}>
                                            <StrikethroughSIcon 
                                                className={formattingIconClassName} 
                                                onClick={() => handleFormatting('strikethrough', 'body')} 
                                            />
                                        </TouchTooltip>
                                    </Box>

                                    <Divider orientation="vertical" flexItem className={styles.formattingToolbarDivider} />

                                    <EmojiPickerButton
                                        textareaRef={textareaRef}
                                        disabled={handleBodyTextDisabled()}
                                        className={styles.formattingIcon}
                                        onSelectEmoji={handleInsertEmoji}
                                    />

                                </Box>
                            </Grid>

                            {/* Add Variable Button */}
                            <Grid item className={styles.addVariableButtonContainer} sx={{ width: "auto" }}>
                                <Button
                                    variant="outlined"
                                    size='small'
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleAddField("body")}
                                    className={`${styles.callToActionButton} ${globalStyles.clickable}`}
                                    disabled={!shouldShowAddVariableButton}
                                    sx={{
                                        opacity: !shouldShowAddVariableButton ? "0.5" : "1",
                                        visibility: !shouldShowAddVariableButton ? "hidden" : "visible",
                                    }}
                                    startIcon={<DataObjectIcon />}
                                >
                                    Add Variable
                                </Button>
                            </Grid>
                            
                        </Grid>
                        
                        {/* Body Textarea */}
                        <TextareaAutosize
                            // disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                            disabled={handleBodyTextDisabled()}
                            ref={(el: HTMLTextAreaElement | null) => {
                                textareaRef.current = el;
                                registerTextarea('body', el);
                            }}
                            className={styles.whatsappTemplateFormBody__bodyTextArea}
                            value={bodyTextValue}
                            onChange={(event: any) => handleBodyTextChange(event.target.value)}
                            onKeyDown={(e) => handleKeyDownForFormatting(e, handleFormatting, "body")}
                            autoComplete='off'
                            placeholder='Enter template body'
                            dir="auto"
                        />

                    </Grid>

                    {showTooManyEmojisError && (
                        <Grid item xs={12} className={globalStyles.marginTop10px}>
                            <VariableDisclaimer text="Marketing templates should not have more than 10 emojis." />
                        </Grid>
                    )}

                    {showConsecutiveNewlinesError && (
                        <Grid item xs={12} className={globalStyles.marginTop10px}>
                            <VariableDisclaimer text="Paragraphs are not supported in the body." />
                        </Grid>
                    )}

                    {/* Invalid Variable Parameter Warning */}
                    {!checkTypeOfVariableValidation('body') && 
                        <Grid item sx={{ marginTop: '5px' }}>
                            {renderInvalidVarParamWarning(createTemplatePayload?.flag_named_parameter)}
                        </Grid>
                    }

                    {/* Too Many Variable Warning */}
                    {checkFlagError("body", createTemplatePayload) &&
                        renderTooManyVariableParameterWarning()
                    }

                    {/* Body Variables Modified Warning */}
                    {areBodyVariablesModified(createTemplatePayload, existingTemplateData) && 
                        renderWarningOfModifiedVar()
                    }

                    {/* Body Text Start or End with Variable Warning */}
                    {doesBodyTextStartOrEndWithVariable(createTemplatePayload) &&
                        renderBodyVariableEdgeWarning()
                    }

                    {/* Authentication Template - Security Recommendation */}
                    {isAuthenticationTemplate && (
                        <Grid item xs={12} sx={{ textAlign: 'end', margin: '10px 0px 0px' }}>
                            <div className={styles.whatsappTemplateFormBody__securityRecommendationContainer}>
                                Add security recommendation
                                <input
                                    type="checkbox"
                                    className={styles.whatsappTemplateFormBody__securityRecommendationCheckbox}
                                    checked={Boolean(createTemplatePayload?.whatsapp_template_data?.whatsapp_template_security_recommendation)}
                                    onChange={(e) => handleSecurityRecommendationChange(e.target.checked)}
                                    disabled={!!createTemplatePayload?.whatsapp_template_id}
                                />
                            </div>
                        </Grid>
                    )}

                    {/* Sample Variables */}
                    <Grid item xs={12} className={globalStyles.marginTop5px}>
                        {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data?.map((field: any, ind: number) => (
                            <React.Fragment key={ind}>
                                <Grid item xs={12} className={globalStyles.marginTop10px}>
                                    <Grid container sx={{ alignItems: 'center' }} flexWrap={"nowrap"}>
                                        
                                        <Grid item className={styles.sampleVariableKeyColumn}>
                                            <SampleVariableKeyLabel
                                                variableKey={`${field?.whatsapp_template_dynamic_variable_key}`}
                                                className={styles.sampleVariableKeyText}
                                            />
                                        </Grid>

                                        <Grid item xs={12} key={field?.whatsapp_template_dynamic_variable_id} sx={{ minWidth: 0, flex: 1 }}>
                                            <TextField
                                                // disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                                                disabled={handleBodySampleVariableTextDisabled()}
                                                size='small'
                                                variant="outlined"
                                                className={styles.sampleVariableTextField}
                                                // value={field.value}
                                                onChange={(e) => handleChange(field?.whatsapp_template_dynamic_variable_key, e, "body")}
                                                fullWidth
                                                autoComplete='off'
                                                value={field?.whatsapp_template_dynamic_variable_sample_value}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none'
                                                    }
                                                }}
                                                inputProps={{ dir: 'auto' }}
                                                InputProps={{
                                                    endAdornment: (field?.whatsapp_template_dynamic_variable_sample_value?.trim() !== '' && !handleBodySampleVariableTextDisabled()) ? (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                edge="end"
                                                                aria-label="Clear sample text"
                                                                onClick={() => handleChange(field?.whatsapp_template_dynamic_variable_key, { target: { value: '' } }, "body")}
                                                                className={globalStyles.clickable}
                                                            >
                                                                <GridCloseIcon className={styles.sampleVariableClearIcon} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ) : null,
                                                }}
                                            />
                                        </Grid>

                                        {(!isTemplateCreated(createTemplatePayload) && !isAuthenticationTemplate) && (
                                            <Grid item sx={{ marginLeft: '5px' }}>
                                                <IconButton 
                                                    onClick={() => handleRemoveField(field.whatsapp_template_dynamic_variable_key, "body")}
                                                    disabled={(createTemplatePayload?.whatsapp_template_id || isAuthenticationTemplate) ? true : false}
                                                    className={styles.sampleVariableDeleteButton}
                                                >
                                                    <Delete className={styles.sampleVariableDeleteIcon} />
                                                </IconButton>
                                            </Grid>
                                        )}

                                    </Grid>
                                </Grid>

                            </React.Fragment>
                        ))}
                    </Grid>

                    {createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data?.filter((data: any) => (data?.whatsapp_template_dynamic_variable_sample_value ?? '').trim() === '').length !== 0 &&
                        <Grid item xs={12} className={globalStyles.marginTop10px}>
                            <VariableDisclaimer text="Add sample text" />
                        </Grid>
                    }
                </Grid>

            </AccordionDetails>
        </Accordion>
    )

}

export default WhatsAppTemplateFormBody;